import { useState, useEffect } from 'react';

/**
 * Hook customizado para gerenciar dados no localStorage
 * 
 * @param {string} key - Chave para armazenar no localStorage
 * @param {any} initialValue - Valor inicial se não houver dados salvos
 * @returns {[any, Function, Function]} - [valor, setValor, removerValor]
 */
export function useLocalStorage(key, initialValue) {
  // Estado para armazenar o valor
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Tentar obter do localStorage
      const item = window.localStorage.getItem(key);
      
      // Parsear JSON armazenado ou retornar initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Erro ao ler localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  /**
   * Atualizar valor no state e no localStorage
   */
  const setValue = (value) => {
    try {
      // Permitir que value seja uma função como no useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      
      // Salvar no state
      setStoredValue(valueToStore);
      
      // Salvar no localStorage
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      
      console.log(`✅ localStorage atualizado: "${key}"`, valueToStore);
    } catch (error) {
      console.error(`❌ Erro ao salvar localStorage key "${key}":`, error);
    }
  };

  /**
   * Remover valor do localStorage e resetar para initialValue
   */
  const removeValue = () => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
      
      console.log(`🗑️ localStorage removido: "${key}"`);
    } catch (error) {
      console.error(`❌ Erro ao remover localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue, removeValue];
}

/**
 * Hook para gerenciar dados temporários de link PIX no localStorage
 * Wrapper específico para facilitar uso com links PIX
 */
export function useLinkPixStorage() {
  const STORAGE_KEY = 'linkPixAleatorio';
  
  const [linkData, setLinkData, removeLinkData] = useLocalStorage(STORAGE_KEY, null);
  
  /**
   * Salvar dados do link PIX
   */
  const salvarLinkPix = (dados) => {
    const dadosCompletos = {
      ...dados,
      timestamp: Date.now(),
      version: '1.0' // Para controle de versão dos dados
    };
    
    setLinkData(dadosCompletos);
  };
  
  /**
   * Verificar se há dados válidos
   */
  const temDados = () => {
    if (!linkData) return false;
    
    // Verificar se os dados não expiraram (24 horas)
    const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24 horas
    const idade = Date.now() - (linkData.timestamp || 0);
    
    if (idade > EXPIRATION_TIME) {
      console.warn('⚠️ Dados do link PIX expiraram (>24h)');
      removeLinkData();
      return false;
    }
    
    return true;
  };
  
  /**
   * Obter dados apenas se válidos
   */
  const obterLinkPix = () => {
    return temDados() ? linkData : null;
  };
  
  return {
    linkData,
    salvarLinkPix,
    obterLinkPix,
    removeLinkData,
    temDados
  };
}

/**
 * Hook para gerenciar dados temporários de cobrança PIX no localStorage
 */
export function useCobrancaPixStorage() {
  const STORAGE_KEY = 'linkPixCobranca';
  
  const [cobrancaData, setCobrancaData, removeCobrancaData] = useLocalStorage(STORAGE_KEY, null);
  
  const salvarCobrancaPix = (dados) => {
    const dadosCompletos = {
      ...dados,
      timestamp: Date.now(),
      version: '1.0'
    };

    // ✅ SALVAR DIRETAMENTE NO LOCALSTORAGE TAMBÉM (garantia)
    try {
      window.localStorage.setItem('linkPixCobranca', JSON.stringify(dadosCompletos));
      console.log('✅ Salvou DIRETAMENTE no localStorage (backup)');
    } catch (error) {
      console.error('❌ Erro ao salvar no localStorage:', error);
    }
    
    // Salvar via hook também
    setCobrancaData(dadosCompletos);
  };
  
  const temDados = () => {
    if (!cobrancaData) return false;
    
    const EXPIRATION_TIME = 24 * 60 * 60 * 1000;
    const idade = Date.now() - (cobrancaData.timestamp || 0);
    
    if (idade > EXPIRATION_TIME) {
      console.warn('⚠️ Dados da cobrança PIX expiraram (>24h)');
      removeCobrancaData();
      return false;
    }
    
    return true;
  };
  
  const obterCobrancaPix = () => {
    return temDados() ? cobrancaData : null;
  };
  
  return {
    cobrancaData,
    salvarCobrancaPix,
    obterCobrancaPix,
    removeCobrancaData,
    temDados
  };
}

/**
 * Hook para gerenciar preferências do usuário
 */
export function usePreferences() {
  const STORAGE_KEY = 'userPreferences';
  
  const defaultPreferences = {
    theme: 'light',
    itemsPerPage: 10,
    notificationsEnabled: true,
    autoRefresh: true,
    refreshInterval: 30000 // 30 segundos
  };
  
  const [preferences, setPreferences, removePreferences] = useLocalStorage(
    STORAGE_KEY, 
    defaultPreferences
  );
  
  /**
   * Atualizar uma preferência específica
   */
  const updatePreference = (key, value) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  /**
   * Resetar todas as preferências para os valores padrão
   */
  const resetPreferences = () => {
    setPreferences(defaultPreferences);
  };
  
  return {
    preferences,
    updatePreference,
    resetPreferences,
    setPreferences
  };
}

export default useLocalStorage;